import { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Switch } from "../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Layers,
  GripVertical,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Type,
  Square,
  Circle,
  Image,
  FileText,
  Grid3X3,
  Table,
  QrCode,
  ChevronUp,
  ChevronDown,
  Trash2,
  Copy,
} from "lucide-react";

// Element type icons
const ELEMENT_ICONS = {
  text: Type,
  rectangle: Square,
  circle: Circle,
  image: Image,
  seyes: FileText,
  grid: Grid3X3,
  table: Table,
  qrcode: QrCode,
};

const ELEMENT_LABELS = {
  text: "Texte",
  rectangle: "Rectangle",
  circle: "Cercle",
  image: "Image",
  seyes: "Seyes",
  grid: "Grille",
  table: "Tableau",
  qrcode: "QR Code",
};

function SortableLayerItem({
  element,
  isSelected,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onDelete,
  onDuplicate,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = ELEMENT_ICONS[element.type] || Square;
  const label = element.text?.substring(0, 20) || ELEMENT_LABELS[element.type] || element.type;
  const isHidden = element.visible === false;
  const isLocked = element.locked === true;

  return (
    <TooltipProvider>
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer ${
          isSelected
            ? "bg-indigo-50 border-indigo-300"
            : isHidden
            ? "bg-slate-50 border-slate-100 opacity-50"
            : "bg-white border-slate-200 hover:border-slate-300"
        }`}
        onClick={() => onSelect(element.id)}
      >
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
        >
          <GripVertical className="w-3 h-3 text-slate-400" />
        </div>

        {/* Icon */}
        <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />

        {/* Label */}
        <span className={`text-sm flex-1 truncate ${isHidden ? "text-slate-400" : ""}`}>
          {label}
        </span>

        {/* Lock indicator */}
        {isLocked && (
          <Lock className="w-3 h-3 text-amber-500" />
        )}

        {/* Actions */}
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => onToggleVisibility(element.id)}
              >
                {isHidden ? (
                  <EyeOff className="w-3 h-3 text-slate-400" />
                ) : (
                  <Eye className="w-3 h-3 text-slate-600" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isHidden ? "Afficher" : "Masquer"}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => onToggleLock(element.id)}
              >
                {isLocked ? (
                  <Lock className="w-3 h-3 text-amber-500" />
                ) : (
                  <Unlock className="w-3 h-3 text-slate-400" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isLocked ? "Deverrouiller" : "Verrouiller"}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}

export function LayersPanel({
  elements,
  selectedId,
  onSelectElement,
  onReorderElements,
  onUpdateElement,
  onDeleteElement,
  onDuplicateElement,
  onBringForward,
  onSendBackward,
}) {
  const [showHidden, setShowHidden] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Elements in reverse order (top layer first)
  const displayElements = [...elements].reverse();
  const filteredElements = showHidden
    ? displayElements
    : displayElements.filter((el) => el.visible !== false);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = displayElements.findIndex((el) => el.id === active.id);
      const newIndex = displayElements.findIndex((el) => el.id === over.id);
      
      // Reverse the indices since we display in reverse order
      const realOldIndex = elements.length - 1 - oldIndex;
      const realNewIndex = elements.length - 1 - newIndex;
      
      const newElements = arrayMove(elements, realOldIndex, realNewIndex);
      onReorderElements(newElements);
    }
  };

  const handleToggleVisibility = (id) => {
    const element = elements.find((el) => el.id === id);
    if (element) {
      onUpdateElement(id, { visible: element.visible === false ? true : false });
    }
  };

  const handleToggleLock = (id) => {
    const element = elements.find((el) => el.id === id);
    if (element) {
      onUpdateElement(id, { locked: !element.locked });
    }
  };

  const selectedElement = elements.find((el) => el.id === selectedId);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium">Calques</span>
          <Badge variant="secondary" className="text-xs">
            {elements.length}
          </Badge>
        </div>
        <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
          <Switch
            checked={showHidden}
            onCheckedChange={setShowHidden}
            className="scale-75"
          />
          Masques
        </label>
      </div>

      {/* Quick actions for selected */}
      {selectedElement && (
        <TooltipProvider>
          <div className="flex items-center gap-1 px-3 py-2 border-b bg-slate-50">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => onBringForward(selectedId)}
                >
                  <ChevronUp className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Avancer</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => onSendBackward(selectedId)}
                >
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reculer</TooltipContent>
            </Tooltip>
            <div className="flex-1" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => onDuplicateElement(selectedId)}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dupliquer</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-red-500 hover:text-red-600"
                  onClick={() => onDeleteElement(selectedId)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Supprimer</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      )}

      {/* Layers list */}
      <ScrollArea className="flex-1">
        {filteredElements.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-400">
            <Layers className="w-8 h-8 mb-2" />
            <p className="text-sm">Aucun element</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredElements.map((el) => el.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="p-2 space-y-1">
                {filteredElements.map((element) => (
                  <SortableLayerItem
                    key={element.id}
                    element={element}
                    isSelected={selectedId === element.id}
                    onSelect={onSelectElement}
                    onToggleVisibility={handleToggleVisibility}
                    onToggleLock={handleToggleLock}
                    onDelete={onDeleteElement}
                    onDuplicate={onDuplicateElement}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </ScrollArea>

      {/* Footer info */}
      {selectedElement && (
        <div className="px-3 py-2 border-t text-xs text-slate-500">
          <div className="flex justify-between">
            <span>Position</span>
            <span>
              {Math.round(selectedElement.x)}, {Math.round(selectedElement.y)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Taille</span>
            <span>
              {Math.round(selectedElement.width || 100)} × {Math.round(selectedElement.height || 100)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
